import { useEffect, useMemo, useRef, useState } from "react";
import { Label } from "@/components/ui/label";
import { ChevronDown, ChevronUp, Loader2, Sparkles, X } from "lucide-react";
import {
  CONCEPTUAL_TAGS,
  CONCEPTUAL_TAG_GROUPS,
  TAG_DESCRIPTIONS,
} from "@/constants/conceptualTags";
import { inferConceptualNodes } from "@/utils/inferConceptualNodes";
import { supabase } from "@/integrations/supabase/client";
import { useHapticFeedback } from "@/hooks/useHapticFeedback";

/**
 * Conceptual node picker for the Log Signal form.
 *
 * Two things were wrong with the previous version.
 *
 * SPACE: it rendered all 36 nodes as one flat two-column grid — eighteen rows
 * of buttons standing between the book you just searched for and the rest of
 * the form. Now the selected nodes show as chips and the full vocabulary sits
 * behind one "Browse all" row, grouped. Nothing was removed; every node is
 * still reachable, and conceptualTags.test.ts fails if one stops being.
 *
 * SILENCE: auto-picking already existed, but it ran silently and swallowed its
 * own errors, so a failed AI call looked exactly like "this book has no obvious
 * nodes" — no spinner, no message, no picks. It also only ever tried the AI, a
 * paid gateway call that can be rate-limited or out of credit. Now the state is
 * always visible and there is a local fallback, so the form stops depending on
 * a remote call to do the thing the user asked for.
 *
 * Every pick is a suggestion: chips remove on click and the browse list toggles
 * freely, so nothing here overrides a deliberate choice.
 */

interface ConceptualTagsSectionProps {
  selectedTags: string[];
  onTagToggle: (tag: string) => void;
  bookTitle?: string;
  bookAuthor?: string;
  bookDescription?: string;
  bookCategories?: string[];
  userTaggingPatterns?: string[];
  isEditMode?: boolean;
}

/** Where the current picks came from — drives the one-line status. */
type AutoState = "idle" | "working" | "ai" | "local" | "none";

/** Matches the AI path's own ceiling, so the fallback can never apply more
 *  nodes than the user would have got from a working gateway. */
const MAX_AUTO = 3;
const MIN_CONFIDENCE = 75;

const ConceptualTagsSection = ({
  selectedTags,
  onTagToggle,
  bookTitle,
  bookAuthor,
  bookDescription,
  bookCategories,
  userTaggingPatterns,
  isEditMode = false,
}: ConceptualTagsSectionProps) => {
  const { selection } = useHapticFeedback();
  const [browseOpen, setBrowseOpen] = useState(false);
  const [autoState, setAutoState] = useState<AutoState>("idle");
  /** tag -> why it was picked, shown on the chip so no pick is unexplained. */
  const [reasons, setReasons] = useState<Record<string, string>>({});

  /** Which book we have already tried, so the effect cannot re-fire on every
   *  keystroke or every toggle. Also means that clearing all the chips does NOT
   *  trigger a re-pick — removing them is a decision, not an invitation. */
  const attemptedFor = useRef<string | null>(null);

  const handleTagToggle = (tag: string) => {
    selection();
    onTagToggle(tag);
  };

  // Latest values without making them effect dependencies: the effect is keyed
  // on title+author, and re-running it because a blurb arrived a tick later
  // would fire a second gateway call for the same book.
  const latest = useRef({ bookDescription, bookCategories, userTaggingPatterns, selectedTags });
  latest.current = { bookDescription, bookCategories, userTaggingPatterns, selectedTags };

  useEffect(() => {
    if (isEditMode) return;                       // editing: respect what is saved
    if (!bookTitle || !bookAuthor) return;        // nothing to reason about yet
    if (latest.current.selectedTags.length > 0) return; // never overwrite a choice

    const key = `${bookTitle} ${bookAuthor}`;
    if (attemptedFor.current === key) return;
    attemptedFor.current = key;

    let cancelled = false;

    const apply = (picks: Array<{ name: string; reason: string }>, source: AutoState) => {
      if (cancelled) return;
      const fresh = picks.filter((p) => !latest.current.selectedTags.includes(p.name));
      if (fresh.length === 0) {
        setAutoState("none");
        return;
      }
      setReasons((prev) => {
        const next = { ...prev };
        for (const p of fresh) next[p.name] = p.reason;
        return next;
      });
      for (const p of fresh) onTagToggle(p.name);
      setAutoState(source);
    };

    (async () => {
      setAutoState("working");

      // 1. Ask the model. It sees the blurb and the provider's categories, so
      //    it beats local matching whenever it is actually reachable.
      try {
        const { data, error } = await supabase.functions.invoke("ai-tag-suggestions", {
          body: {
            title: bookTitle,
            author: bookAuthor,
            description: latest.current.bookDescription,
            currentTags: [],
            userTaggingPatterns: latest.current.userTaggingPatterns,
          },
        });
        if (cancelled) return;
        if (!error && Array.isArray(data?.suggestions)) {
          const picks = data.suggestions
            .filter(
              (s: { name: string; confidence: number }) =>
                s.confidence >= MIN_CONFIDENCE &&
                (CONCEPTUAL_TAGS as readonly string[]).includes(s.name),
            )
            .slice(0, MAX_AUTO)
            .map((s: { name: string; reason?: string; confidence: number }) => ({
              name: s.name,
              reason: s.reason || `suggested with ${s.confidence}% confidence`,
            }));
          if (picks.length > 0) {
            apply(picks, "ai");
            return;
          }
        }
      } catch {
        // Deliberately quiet: an unreachable gateway is not the user's problem,
        // and the fallback below is the answer to it. The visible state still
        // changes, which is the part that was missing before.
      }
      if (cancelled) return;

      // 2. Fall back to the curated author map and distinctive phrases in the
      //    blurb. Conservative by design — see inferConceptualNodes.
      const local = inferConceptualNodes({
        author: bookAuthor,
        description: latest.current.bookDescription,
        categories: latest.current.bookCategories,
      });
      if (local.length > 0) {
        apply(local, "local");
        return;
      }
      setAutoState("none");
    })();

    return () => {
      cancelled = true;
    };
    // onTagToggle is intentionally omitted: BookFormModal recreates it on every
    // render, so depending on it would restart this on each keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookTitle, bookAuthor, isEditMode]);

  const status = useMemo(() => {
    const n = selectedTags.length;
    switch (autoState) {
      case "working":
        return (
          <span className="flex items-center gap-1.5 text-xs text-slate-500">
            <Loader2 className="w-3 h-3 animate-spin" />
            Reading the book…
          </span>
        );
      case "ai":
        return (
          <span className="flex items-center gap-1.5 text-xs text-blue-300/80">
            <Sparkles className="w-3 h-3" />
            {n} auto-picked
          </span>
        );
      case "local":
        return (
          <span className="flex items-center gap-1.5 text-xs text-blue-300/80">
            <Sparkles className="w-3 h-3" />
            {n} suggested
          </span>
        );
      default:
        return null;
    }
  }, [autoState, selectedTags.length]);

  return (
    <div>
      <div className="flex items-center justify-between mb-2 gap-3">
        <Label className="text-slate-300 text-sm">Conceptual Nodes</Label>
        {status}
      </div>

      {selectedTags.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {selectedTags.map((tag) => (
            <button
              key={tag}
              type="button"
              onClick={() => handleTagToggle(tag)}
              title={reasons[tag] || TAG_DESCRIPTIONS[tag] || undefined}
              aria-label={`Remove ${tag}`}
              className="group flex items-center gap-1.5 px-3 py-2 rounded-full text-xs bg-blue-500/20 text-blue-300 border border-blue-500/30 transition-colors active:scale-95 hover:border-blue-400/60"
            >
              {tag}
              <X className="w-3 h-3 opacity-50 group-hover:opacity-100 transition-opacity" />
            </button>
          ))}
        </div>
      ) : (
        <p className="text-slate-500 text-xs py-1">
          {autoState === "working"
            ? "Looking for the nodes that fit this book…"
            : "No nodes yet — browse below to add them."}
        </p>
      )}

      <button
        type="button"
        onClick={() => setBrowseOpen((o) => !o)}
        aria-expanded={browseOpen}
        className="mt-3 flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-200 transition-colors"
      >
        {browseOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        {/* Counted from the vocabulary itself so this can never claim a number
            that has drifted from what the list actually contains. */}
        Browse all {CONCEPTUAL_TAGS.length} nodes
      </button>

      {browseOpen && (
        <div className="mt-3 space-y-4 animate-in fade-in-50 slide-in-from-top-1 duration-200">
          {CONCEPTUAL_TAG_GROUPS.map((group) => (
            <div key={group.label}>
              <p className="text-[10px] uppercase tracking-[0.14em] text-slate-500 mb-2">
                {group.label}
              </p>
              <div className="grid grid-cols-2 gap-2">
                {group.tags.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => handleTagToggle(tag)}
                    title={TAG_DESCRIPTIONS[tag] || undefined}
                    aria-pressed={selectedTags.includes(tag)}
                    className={`px-3 py-2 rounded-full text-xs transition-colors active:scale-95 ${
                      selectedTags.includes(tag)
                        ? "bg-blue-500/20 text-blue-300 border border-blue-500/30"
                        : "bg-slate-700 text-slate-400 border border-slate-600 hover:bg-slate-600"
                    }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ConceptualTagsSection;
